import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Copy, Send, Sparkles, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_STYLES = {
  Payments: 'bg-emerald-100 text-emerald-700',
  Documents: 'bg-sky-100 text-sky-700',
  'Pre-departure': 'bg-amber-100 text-amber-700',
  'During trip': 'bg-violet-100 text-violet-700',
  'Post-trip': 'bg-teal-100 text-teal-700',
  'Lead nurturing': 'bg-rose-100 text-rose-700',
  Operational: 'bg-slate-100 text-slate-600',
};

// Replace {tokens} with resolved values; keep the placeholder when no value.
const fillBody = (body, values) =>
  (body || '').replace(/\{(\w+)\}/g, (m, k) => {
    const v = values?.[k];
    return v != null && String(v).trim() !== '' ? String(v) : m;
  });

const norm = (s) => (s || '').toLowerCase();

// Auto-detect the most relevant template by a caller-supplied hint.
const PICK = {
  departure: ['departure tomorrow', 'departure', 'check-in'],
  payment: ['partial payment received', 'payment reminder', 'payment', 'full payment'],
  quote: ['quote expiry', 'quote'],
  enquiry: ['enquiry acknowledgment', 'enquiry', 'quote'],
};
const pickTemplate = (templates, hint) => {
  if (!hint) return null;
  for (const key of (PICK[hint] || [])) {
    const found = templates.find((t) => norm(t.name).includes(key));
    if (found) return found;
  }
  return null;
};

// Module-level so the visual-edits Babel plugin never recurses into a nested
// member-expression `.map()` inside JSX (known build-crash trigger).
const TemplateGroup = ({ cat, rows }) => {
  const items = rows || [];
  return (
    <SelectGroup>
      <SelectLabel>{cat}</SelectLabel>
      {items.map((t) => (
        <SelectItem key={t.id} value={t.id} data-testid={`wa-template-opt-${t.id}`}>{t.name}</SelectItem>
      ))}
    </SelectGroup>
  );
};

/**
 * SendWhatsAppDialog — quick WhatsApp send from a trip/lead.
 * - auto-detects a relevant template (via `suggestHint`)
 * - template selector (all templates, grouped by category)
 * - variables auto-filled from `context`, editable preview, Copy + Open WhatsApp
 *
 * Renders as a bottom sheet on mobile, centered dialog on desktop.
 */
const SendWhatsAppDialog = ({ open, onOpenChange, title, phone, context = {}, suggestHint = null }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true); setTouched(false);
    axios.get(`${BACKEND_URL}/api/message-templates`)
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setTemplates(list);
        const suggested = pickTemplate(list, suggestHint);
        if (suggested) {
          setSelectedId(suggested.id);
          setMessage(fillBody(suggested.body, context));
        } else {
          setSelectedId('');
          setMessage('');
        }
      })
      .catch(() => { if (alive) toast.error('Failed to load message templates'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, suggestHint]);

  const suggested = useMemo(() => pickTemplate(templates, suggestHint), [templates, suggestHint]);
  const selected = useMemo(() => templates.find((t) => t.id === selectedId) || null, [templates, selectedId]);

  const grouped = useMemo(() => {
    const g = {};
    templates.forEach((t) => { (g[t.category] = g[t.category] || []).push(t); });
    return Object.keys(g).map((cat) => ({ cat, rows: g[cat] }));
  }, [templates]);

  const chooseTemplate = (id) => {
    setSelectedId(id);
    const t = templates.find((x) => x.id === id);
    if (t) { setMessage(fillBody(t.body, context)); setTouched(false); }
  };

  const useSuggested = () => { if (suggested) chooseTemplate(suggested.id); };

  const copyMsg = async () => {
    if (!message.trim()) { toast.error('Pick a template or type a message first'); return; }
    try { await navigator.clipboard.writeText(message); toast.success('Message copied'); }
    catch { toast.error('Copy failed'); }
  };
  const openWhatsApp = () => {
    if (!message.trim()) { toast.error('Pick a template or type a message first'); return; }
    const num = String(phone || '').replace(/[^0-9]/g, '');
    const base = num ? `https://wa.me/${num}` : 'https://wa.me/';
    window.open(`${base}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="send-whatsapp-dialog"
        className="max-w-lg w-full max-h-[88vh] overflow-y-auto top-auto bottom-0 translate-y-0 rounded-b-none rounded-t-2xl sm:top-[50%] sm:bottom-auto sm:-translate-y-1/2 sm:rounded-lg"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4 text-slate-500" /> WhatsApp Web
            <span className="text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">Quick link · not tracked</span>
          </DialogTitle>
          <DialogDescription>{title ? `Opens WhatsApp Web to message ${title}. This is a quick link and is NOT logged in the CRM — use “WhatsApp — Official” to keep a record.` : 'Opens WhatsApp Web with your message. NOT logged in the CRM.'}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading templates…</div>
        ) : (
          <div className="space-y-4 py-1">
            {/* Suggested template banner */}
            {suggested && suggested.id !== selectedId && (
              <button
                type="button"
                onClick={useSuggested}
                data-testid="wa-suggested-template"
                className="w-full flex items-center gap-2 text-left rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 hover:bg-amber-100 transition-colors"
              >
                <Sparkles className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="text-sm text-amber-900">Suggested: <span className="font-semibold">{suggested.name}</span> — tap to use</span>
              </button>
            )}

            {/* Template selector */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Template</Label>
              <Select value={selectedId} onValueChange={chooseTemplate}>
                <SelectTrigger className="h-10" data-testid="wa-template-select"><SelectValue placeholder="Choose a message template…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {grouped.map((grp) => (
                    <TemplateGroup key={grp.cat} cat={grp.cat} rows={grp.rows} />
                  ))}
                </SelectContent>
              </Select>
              {selected && (
                <Badge className={`${CATEGORY_STYLES[selected.category] || CATEGORY_STYLES.Operational} border-0 text-[11px] mt-2`}>{selected.category}</Badge>
              )}
            </div>

            {/* Editable preview */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Message (edit before sending)</Label>
              <Textarea
                value={message}
                onChange={(e) => { setMessage(e.target.value); setTouched(true); }}
                rows={7}
                placeholder="Select a template above to auto-fill, or type your own message…"
                className="text-sm leading-relaxed bg-[#e7ffdb]/40 border-emerald-200"
                data-testid="wa-message-preview"
              />
              {!phone && <p className="text-xs text-amber-600 mt-1.5">No phone on file — WhatsApp will open without a recipient.</p>}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={copyMsg} data-testid="wa-copy-btn"><Copy className="h-4 w-4 mr-1.5" /> Copy</Button>
          <Button onClick={openWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-open-btn"><Send className="h-4 w-4 mr-1.5" /> Open WhatsApp</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendWhatsAppDialog;
